import { useEffect } from 'react';

// Scroll-reveal: fades + slides any `.reveal` element into view as it enters
// the viewport, with a small staggered delay between siblings. Mirrors the
// IntersectionObserver behaviour from the design handoff. Falls back to
// force-revealing everything if IO is unavailable or never fires.
//
// Elements that appear *after* mount are picked up too. That is not a
// hypothetical: switching language re-keys the lists whose keys are the
// translated text, so React unmounts those nodes and mounts new ones — the
// original observer was left watching nodes no longer in the document, and six
// feature cards stayed at opacity 0 until a reload.
export default function useReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reveal = (el) => {
      if (!el || el.__shown) return;
      el.__shown = true;
      const sibs = el.parentNode
        ? Array.from(el.parentNode.children).filter((c) => c.classList && c.classList.contains('reveal'))
        : [];
      const idx = Math.max(0, sibs.indexOf(el));
      el.style.transitionDelay = `${Math.min(idx, 6) * 0.085}s`;
      el.classList.add('is-visible');
    };

    const inView = (el) => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh * 0.92 && r.bottom > 0;
    };

    const pending = () => Array.from(root.querySelectorAll('.reveal')).filter((el) => !el.__shown);

    // 2) IntersectionObserver drives the staggered reveal below the fold.
    let io;
    try {
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { reveal(e.target); io.unobserve(e.target); } });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    } catch { /* IO unsupported — scroll + safety net cover it */ }

    // 1) Anything already on screen shows at once; the rest waits for the
    //    observer. Runs for the initial markup and for every later addition.
    const track = (els) => els.forEach((el) => {
      if (el.__shown) return;
      if (inView(el)) reveal(el);
      else if (io) io.observe(el);
      // No IntersectionObserver: show it rather than animate it. The safety net
      // below only covers the initial markup, so a node added later would
      // otherwise depend on the visitor happening to scroll.
      else reveal(el);
    });

    track(pending());

    // 3) Nodes React mounts later — a language switch, or anything else that
    //    replaces a subtree — arrive here rather than staying invisible.
    const mo = new MutationObserver((records) => {
      const added = records.some((r) => r.addedNodes.length > 0);
      if (added) track(pending());
    });
    mo.observe(root, { childList: true, subtree: true });

    // 4) Lightweight scroll backup in case IO never fires.
    const onScroll = () => track(pending());
    window.addEventListener('scroll', onScroll, { passive: true });

    // 5) Hard safety net: nothing from the initial markup may stay invisible.
    const safety = setTimeout(() => pending().forEach(reveal), 1400);

    return () => {
      if (io) io.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', onScroll);
      clearTimeout(safety);
    };
  }, [rootRef]);
}
